import React, { useEffect } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { useAppSelector } from 'hooks/useAppSelector';
import { BorrowerRole } from 'enums/BorrowerRole';
import {
  getEmploymentVariableName,
  getIsAddressInformationDisabled,
  getIsInitialValueNotNull,
} from 'pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.utils';
import {
  PreviousEmploymentInputLabel,
  PreviousEmploymentVariable,
} from 'pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.enums';
import { getErrorMessageForRequiredFields } from 'utils/stringUtils';
import { getMessageForInvalidFields, validatePhoneNumber } from 'utils/validationUtils';
import { isValidDateBeforeNow } from 'utils/dateUtils';
import { DateFormat } from 'enums/DateFormat';
import { addressRegistration } from 'utils/registrationUtils';
import Input from 'components/Inputs/Input';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import NumberInput from 'components/Inputs/NumberInput';
import { SearchLocationInput } from 'components/SearchLocationInput';
import styles from 'pages/Dashboard/steps/InformationTabs/InformationTabs.module.scss';
import Select from 'components/Select';
import { OptionType } from 'utils/inputSelect';
import { EMPLOYMENT_STATUS_OPTIONS } from 'pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.constants';

const PreviousEmploymentInformation = ({
  formMethods,
  borrowerType,
  watcher,
}: {
  formMethods: UseFormReturn<any, any>;
  borrowerType: BorrowerRole;
  watcher: any;
}) => {
  const { variables } = useAppSelector((state) => state.application);
  const applicationVariables = variables!;

  const {
    register,
    clearErrors,
    formState: { errors },
    setValue,
    trigger,
    formState,
    getValues,
    control,
  } = formMethods;

  useEffect(() => {
    register(getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType), {
      required: {
        value: !watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType)],
        message: getErrorMessageForRequiredFields(PreviousEmploymentInputLabel.EmployerName),
      },
    });
    register(getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType), {
      required: {
        value: !watcher[getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType)],
        message: getErrorMessageForRequiredFields(PreviousEmploymentInputLabel.Position),
      },
    });
    register(getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType), {
      required: {
        value: !watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType)],
        message: getErrorMessageForRequiredFields(PreviousEmploymentInputLabel.EmployerPhone),
      },
      validate: (value) => {
        return validatePhoneNumber(value);
      },
    });
    register(getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType), {
      required: {
        value: !watcher[getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)],
        message: getErrorMessageForRequiredFields(PreviousEmploymentInputLabel.HireDate),
      },
      validate: (value) => {
        return (
          isValidDateBeforeNow(value, DateFormat.MonthYear) ||
          getMessageForInvalidFields(PreviousEmploymentInputLabel.HireDate)
        );
      },
    });
    register(getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType), {
      required: {
        value: !watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType)],
        message: getErrorMessageForRequiredFields(PreviousEmploymentInputLabel.EmploymentStatus),
      },
    });
    addressRegistration(
      register,
      {
        streetName: getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType),
        cityName: getEmploymentVariableName(PreviousEmploymentVariable.City, borrowerType),
        stateName: getEmploymentVariableName(PreviousEmploymentVariable.State, borrowerType),
        zipName: getEmploymentVariableName(PreviousEmploymentVariable.Zip, borrowerType),
      },
      {
        streetLabel: PreviousEmploymentInputLabel.Address,
        cityLabel: PreviousEmploymentInputLabel.City,
        stateLabel: PreviousEmploymentInputLabel.State,
        zipLabel: PreviousEmploymentInputLabel.Zip,
      },
    );
  }, []);

  return (
    <div className={styles.employmentContainer}>
      <div className={styles.formRow}>
        <Select
          useFormControl
          required
          control={control}
          inputContainerClassName={styles.input}
          onChange={(event: any) => {
            clearErrors(getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType));
          }}
          onBlur={() => {
            if (watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType)]) {
              trigger(getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType));
            }
          }}
          name={getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType)}
          label={PreviousEmploymentInputLabel.EmploymentStatus}
          isDisabled={getIsInitialValueNotNull(
            applicationVariables[getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType)],
          )}
          errorMessage={
            errors[getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType)]?.message
          }
          options={EMPLOYMENT_STATUS_OPTIONS}
          value={EMPLOYMENT_STATUS_OPTIONS.find(
            (option: OptionType) =>
              watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmploymentStatus, borrowerType)] ===
              option.value,
          )}
        />
        <NumberInput
          label={PreviousEmploymentInputLabel.HireDate}
          name={getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)}
          value={watcher[getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)]}
          errorMessage={errors[getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)]?.message}
          disabled={getIsInitialValueNotNull(
            applicationVariables[getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)],
          )}
          className={styles.input}
          onChange={(event) => {
            clearErrors(getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType));
            setValue(getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType), event.target.value);
          }}
          onBlur={() => {
            if (watcher[getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)]) {
              trigger(getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType));
            }
          }}
          placeholder="MM/YYYY"
          format="##/####"
          mask="_"
          allowEmptyFormatting
        />
      </div>
      <div className={styles.formRow}>
        <Input
          label={PreviousEmploymentInputLabel.EmployerName}
          name={getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType)}
          value={watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType)]}
          required={false}
          errorMessage={
            errors[getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType)]?.message
          }
          inputContainerClassName={styles.input}
          onChange={(event) => {
            clearErrors(getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType));
            setValue(
              getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType),
              event.target.value,
            );
          }}
          disabled={getIsInitialValueNotNull(
            applicationVariables[getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType)],
          )}
        />
        <Input
          label={PreviousEmploymentInputLabel.Position}
          name={getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType)}
          value={watcher[getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType)]}
          errorMessage={errors[getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType)]?.message}
          inputContainerClassName={styles.input}
          disabled={getIsInitialValueNotNull(
            applicationVariables[getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType)],
          )}
          onChange={(event) => {
            clearErrors(getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType));
            setValue(getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType), event.target.value);
          }}
        />
        <PhoneNumberInput
          label={PreviousEmploymentInputLabel.EmployerPhone}
          name={getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType)}
          className={styles.input}
          inputContainerClassname={styles.input}
          value={watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType)]}
          errorMessage={
            errors[getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType)]?.message
          }
          disabled={getIsInitialValueNotNull(
            applicationVariables[getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType)],
          )}
          onChange={(value: string) => {
            clearErrors(getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType));
            setValue(getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType), value);
          }}
          onBlur={() => {
            if (watcher[getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType)]) {
              trigger(getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType));
            }
          }}
        />
      </div>
      <SearchLocationInput
        containerClassName={styles.select_input}
        initialValue={{
          streetAddress: watcher[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)],
          state: watcher[getEmploymentVariableName(PreviousEmploymentVariable.State, borrowerType)],
          city: watcher[getEmploymentVariableName(PreviousEmploymentVariable.City, borrowerType)],
          zip: watcher[getEmploymentVariableName(PreviousEmploymentVariable.Zip, borrowerType)],
        }}
        streetLabel={PreviousEmploymentInputLabel.Address}
        zipLabel={PreviousEmploymentInputLabel.Zip}
        cityLabel={PreviousEmploymentInputLabel.City}
        stateLabel={PreviousEmploymentInputLabel.State}
        control={control}
        useFormControl
        inputNames={{
          streetAddress: getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType),
          city: getEmploymentVariableName(PreviousEmploymentVariable.City, borrowerType),
          state: getEmploymentVariableName(PreviousEmploymentVariable.State, borrowerType),
          zip: getEmploymentVariableName(PreviousEmploymentVariable.Zip, borrowerType),
        }}
        required
        errors={{
          streetAddress: errors[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)],
          state: errors[getEmploymentVariableName(PreviousEmploymentVariable.State, borrowerType)],
          city: errors[getEmploymentVariableName(PreviousEmploymentVariable.City, borrowerType)],
          zip: errors[getEmploymentVariableName(PreviousEmploymentVariable.Zip, borrowerType)],
        }}
        inputChange={(name, value) => {
          setValue(name as any, value);

          if (formState.isSubmitted) {
            trigger(name as any);
          }
        }}
        getValues={getValues}
        disabled={getIsAddressInformationDisabled({
          city: applicationVariables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]?.city,
          state_or_province:
            applicationVariables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]
              ?.state_or_province,
          street_name:
            applicationVariables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]
              ?.street_name,
          zip_or_postal_code:
            applicationVariables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]
              ?.zip_or_postal_code,
        })}
      />
    </div>
  );
};

export default PreviousEmploymentInformation;
